// FEWD-336: the legacy location-SEO and directory pages are discontinued.
// These suffixes match the new app's /rankings/{viewType} values 1:1
// (utr-web-app src/app/(public)/(directory)/locations/viewTypes.ts) — keep
// the two lists in sync if a view type is ever added.
export const SEO_LOCATION_VIEW_SUFFIXES = [
    'mens-tennis-rankings',
    'womens-tennis-rankings',
    'boys-tennis-rankings',
    'girls-tennis-rankings',
    'mens-pickleball-rankings',
    'womens-pickleball-rankings',
    'boys-pickleball-rankings',
    'girls-pickleball-rankings',
    'tennis-tournaments',
    'pickleball-tournaments',
    'tennis-leagues',
    'pickleball-leagues',
];

export const SEO_LOCATION_ROUTE_REGEX = new RegExp(
    `^/([^/]+(?:/[^/]+){0,2})/(${SEO_LOCATION_VIEW_SUFFIXES.join('|')})/?$`,
);

export const LEGACY_DIRECTORY_PATHS = [
    '/countries',
    '/states',
    '/cities',
    '/cities/:letter',
    '/directory',
];

const STATIC_DIRECTORY_REDIRECTS = {
    '/countries': '/directory/countries',
    '/states': '/directory/states',
    '/cities': '/directory/cities/a',
};

const CITIES_LETTER_REGEX = /^\/cities\/([^/]+)\/?$/;

const NEW_APP_PATH_PREFIXES = ['/rankings', '/locations', '/directory'];

// Express's default non-strict routing lets /countries/ reach the /countries
// handler with the slash preserved on req.path — normalize before branching so
// the trailing-slash variants don't fall through to the /locations catch-all
// (which 404s on the new app).
const normalizePath = pathname => (
    pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
);

// A path already in the new app's shape must NOT be redirected. Since the
// FEWD-336 hotfix, prod's nextAppHost IS the public host, so redirecting such a
// path sends it to itself and loops forever. Front Door is what gets these to
// the new origin; legacy's job is simply not to claim them.
export const isNewAppShapePath = (pathname) => {
    const normalizedPath = normalizePath(pathname);
    return NEW_APP_PATH_PREFIXES.some(
        prefix => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
    );
};

// Both redirect sinks build their target from the request path, so a path that
// is absolute ('https://evil.com') or protocol-relative ('//evil.com') would
// resolve away from nextAppHost and carry the visitor off-site. Resolving
// against the configured host and comparing origins rejects exactly those,
// and returns null so the caller can fall through instead of redirecting.
export const sameOriginRedirectUrl = (pathAndSearch, nextAppHost) => {
    let base;
    let target;
    try {
        base = new URL(nextAppHost);
        target = new URL(pathAndSearch, base);
    } catch {
        return null;
    }
    if (target.origin !== base.origin) {
        return null;
    }
    return target.toString();
};

// Callers MUST reject `isNewAppShapePath` before calling this — both do. The
// branch below is a safety net, not the guard: returning the path unchanged is
// the least-bad answer for a caller that forgot, but on prod (where nextAppHost
// is the public host) redirecting to it is the self-redirect loop this fix
// exists to stop. Keep the check at the call site.
export const seoLocationRedirectPath = (pathname) => {
    const normalizedPath = normalizePath(pathname);
    if (isNewAppShapePath(normalizedPath)) {
        return normalizedPath;
    }
    if (STATIC_DIRECTORY_REDIRECTS[normalizedPath]) {
        return STATIC_DIRECTORY_REDIRECTS[normalizedPath];
    }
    const citiesLetterMatch = CITIES_LETTER_REGEX.exec(normalizedPath);
    if (citiesLetterMatch) {
        return `/directory/cities/${citiesLetterMatch[1]}`;
    }
    const suffixedMatch = SEO_LOCATION_ROUTE_REGEX.exec(normalizedPath);
    if (suffixedMatch) {
        return `/rankings/${suffixedMatch[2]}/${suffixedMatch[1]}`;
    }
    return `/locations${normalizedPath}`;
};
